<script setup lang="ts">
import { onBeforeMount, PropType, ref, Ref, toRefs, computed } from "vue";
import { useApiClient } from "@/api/client";
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";
import {
	required,
	email,
	phoneNumber,
	maxLength,
	minLength,
} from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import { loadingWrapperAll } from "@/api/helpers/loadingWrapper";
import { useBaseStore } from "@/stores/base";
import ShowPasswordConfirmation from "@/modules/users/components/ShowPasswordConfirmation.vue";
import { AUTH_MODULES } from "@/ui/Enums";
import UpdatePasswordDialog from "@/modules/users/components/UpdatePasswordDialog.vue";
import { passwordFieldsType } from "@/api/resources/users";

const { t } = useI18n();

const props = defineProps({
	user: {
		type: Object as PropType<App.Data.UserDO>,
		require: true,
	},
	asyncUpdateFunction: {
		type: Function,
		required: true,
	},
	asyncUpdatePasswordFunction: {
		type: Function,
		required: true,
	},
	isProfilePage: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(["update-user"]);

const { user } = toRefs(props);

const isSaving: Ref<boolean> = ref(false);
const definedCompanies: Ref<[]> = ref([]);
const availableRoles: Ref<App.Data.RoleDO[]> = ref();
const availableLanguages: Ref<App.Data.LanguageDO[]> = ref();

const isMotivesAuthentication = computed(
	() => user.value.authentication_module === AUTH_MODULES.MOTIVES_NATIVE
);

const loadUserDependencies = async () => {
	const [rolesResponse, definedCompaniesResponse, languagesResponse] =
		await loadingWrapperAll([
			useApiClient("roles").getRoles({ per_page: 100 }),
			useApiClient("users").getDefinedCompanies(),
			useApiClient("environment").getLanguages(),
		]);

	if (rolesResponse.isOk()) {
		availableRoles.value = rolesResponse.value.data;
	}

	if (definedCompaniesResponse.isOk()) {
		definedCompanies.value = definedCompaniesResponse.value;
	}

	if (languagesResponse.isOk()) {
		availableLanguages.value = languagesResponse.value;
	}
};

onBeforeMount(() => {
	loadUserDependencies();
	get2FAInformation();
});

const updateUser = async () => {
	isSaving.value = true;

	await props.asyncUpdateFunction(user.value);

	isSaving.value = false;
};

const openExpansionPanels = ref([0, 1, 2]);
const isFormValid: Ref<boolean> = ref(true);

const showSetPasswordConfirmation = ref(false);

const configuringTwoFactor = ref(false);
const executeCallback = ref(false);

const qrCode = ref("");
const recoveryCodes = ref("");
const totpCode = ref("");

const wrong2FACode = ref(false);

const clearWrong2FACode = () => {
	wrong2FACode.value = false;
};

const get2FAInformation = async () => {
	if (props.isProfilePage) {
		if (isMotivesAuthentication.value) {
			const [qrResponse, recoveryCodesResponse] = await loadingWrapperAll([
				useApiClient("users").getTwoFactorQRCode(),
				useApiClient("users").getTwoFactorRecoveryCodes(),
			]);

			if (qrResponse.isOk()) {
				qrCode.value = qrResponse.value.svg;
			}

			if (recoveryCodesResponse.isOk()) {
				recoveryCodes.value = recoveryCodesResponse.value;
			}

			if (qrResponse.isErr()) {
				if (qrResponse.error.status === 423) {
					showSetPasswordConfirmation.value = true;
				}
			}
		}
	}
};

const enable2FA = async () => {
	const response = await useApiClient("users").enableTwoFactorAuthentication();
	if (response.isOk()) {
		await get2FAInformation();
		configuringTwoFactor.value = true;
	}
	if (response.isErr()) {
		if (response.error.status === 423) {
			showSetPasswordConfirmation.value = true;
		}
	}
};

const confirm2FA = async () => {
	const response = await useApiClient("users").confirmedTwoFactorAuthentication(
		totpCode.value
	);
	totpCode.value = "";
	if (response.isOk()) {
		await props.asyncUpdateFunction(user.value);
		configuringTwoFactor.value = false;
	}
	if (response.isErr()) {
		if (response.error.status === 423) {
			showSetPasswordConfirmation.value = true;
		}
		if (response.error.status === 422) {
			wrong2FACode.value = true;
			setTimeout(clearWrong2FACode, 5000);
		}
	}
};

// const enable2FA = async () => {
// 	const response = await useApiClient("users").enableTwoFactorAuthentication();
// 	if (response.isOk()) {
// 		await props.asyncUpdateFunction(user.value);
// 		await get2FAInformation();
// 	}
// 	if (response.isErr()) {
// 		if (response.error.status === 423) {
// 			showSetPasswordConfirmation.value = true;
// 		}
// 	}
// };

const disable2FA = async () => {
	const response = await useApiClient("users").disableTwoFactorAuthentication();
	if (response.isOk()) {
		await props.asyncUpdateFunction(user.value);
	}
	if (response.isErr()) {
		if (response.error.status === 423) {
			showSetPasswordConfirmation.value = true;
		}
	}
};

const updatePassword = async (passwordFields: passwordFieldsType) => {
	const response = await props.asyncUpdatePasswordFunction(passwordFields);
	if (response.isOk()) {
		showUpdatePasswordDialog.value = false;
	}
};

const showUpdatePasswordDialog = ref(false);
</script>

<template>
	<update-password-dialog
		v-if="showUpdatePasswordDialog"
		v-model="showUpdatePasswordDialog"
		:async-update-function="updatePassword"
		:is-profile-page="isProfilePage"
	/>
	<v-alert
		v-if="
			isProfilePage &&
			!user.has2FA &&
			useBaseStore().currentEnvironment.is_two_factor_authentication_required &&
			isMotivesAuthentication
		"
		border="start"
		color="error"
		variant="tonal"
		icon="mdi-alert-circle"
		prominent
	>
		The administrator of this application requires you to enable 2FA in order
		for you to use the application. Enable 2FA below.
	</v-alert>
	<module-loading v-if="user">
		<v-form v-model="isFormValid">
			<v-row>
				<v-col cols="12" md="12">
					<v-expansion-panels v-model="openExpansionPanels" multiple>
						<v-expansion-panel class="mt-5">
							<v-expansion-panel-title>
								<span class="text-h6">{{
									$t("users.overview.sections.personalInformation")
								}}</span>
							</v-expansion-panel-title>
							<v-expansion-panel-text>
								<div class="d-flex flex-column ga-3">
									<v-text-field
										v-model="user.name"
										:label="t('users.overview.modal.inputName')"
										prepend-icon="mdi-account-outline"
										:rules="[required(t), maxLength(t, 255), minLength(t, 3)]"
										clearable
										hide-details="auto"
									/>
									<v-text-field
										v-model="user.email"
										:label="t('users.overview.modal.inputEmail')"
										placeholder="your.email@example.com"
										prepend-icon="mdi-at"
										:rules="[required(t), email(t)]"
										clearable
										hide-details="auto"
									/>
									<v-text-field
										v-model="user.phone_number"
										:label="t('users.overview.modal.inputPhoneNumber')"
										placeholder="+310612345678"
										prepend-icon="mdi-cellphone"
										:rules="[
											...(useBaseStore().HAS_ENVIRONMENT_FEATURE(
												'user_must_provide_phone_number'
											)
												? [required(t)]
												: []),
											phoneNumber(t),
										]"
										clearable
										hide-details="auto"
									/>
									<v-combobox
										v-model="user.company"
										:items="definedCompanies"
										:label="t('users.overview.modal.inputCompany')"
										prepend-icon="mdi-domain"
										:rules="[required(t)]"
										hide-details="auto"
									/>
								</div>
							</v-expansion-panel-text>
						</v-expansion-panel>
						<v-expansion-panel
							class="mt-5"
							elevation="2"
							v-if="isMotivesAuthentication"
						>
							<v-expansion-panel-title>
								<span class="text-h6">Password</span>
							</v-expansion-panel-title>
							<v-expansion-panel-text class="pt-7">
								<v-row>
									<v-col cols="12">
										<div class="d-flex">
											<div class="pt-2">
												Change your password by clicking the 'update password'
												button:
											</div>
											<v-spacer />
											<div>
												<v-btn
													variant="flat"
													color="secondary"
													@click="showUpdatePasswordDialog = true"
												>
													Update Password
												</v-btn>
											</div>
										</div>
									</v-col>
								</v-row>
							</v-expansion-panel-text>
						</v-expansion-panel>
						<v-expansion-panel
							class="mt-5"
							v-if="isMotivesAuthentication"
						>
							<v-expansion-panel-title>
								<span class="text-h6">Two Factor Authentication</span>
							</v-expansion-panel-title>
							<v-expansion-panel-text class="pt-7">
								<v-row v-if="isProfilePage">
									<v-col cols="12" v-if="user.has2FA" class="d-flex">
										<show-password-confirmation
											v-if="showSetPasswordConfirmation"
											:is-visible="showSetPasswordConfirmation"
											:on-success="
												executeCallback ? disable2FA : get2FAInformation
											"
											@close-dialog="showSetPasswordConfirmation = false"
										/>
										<v-card
											elevation="2"
											class="d-flex align-center justify-space-between mb-4"
										>
											<v-card-text>
												<v-icon class="mr-2"> mdi-lock </v-icon>
												Two factor authentication is enabled on your account
											</v-card-text>
											<v-card-actions>
												<v-spacer></v-spacer>
												<v-btn
													color="error"
													variant="tonal"
													@click="disable2FA(), (executeCallback = true)"
													>Disable 2FA
												</v-btn>
											</v-card-actions>
										</v-card>
									</v-col>
									<v-col v-else-if="configuringTwoFactor && !user.has2FA">
										<v-card>
											<v-alert
												type="info"
												variant="tonal"
												density="default"
												class="ma-4"
											>
												Two-factor authentication is not enabled yet. Please
												scan the QR code and enter the code from your
												authenticator app to complete your setup.
												<b
													>Save these recovery codes somewhere safe. You won't
													see them again!</b
												>
											</v-alert>
											<v-divider
												style="border-color: unset !important"
											></v-divider>
											<div class="d-flex">
												<div>
													<v-card-title class="text-center">
														QR-Code
													</v-card-title>
													<v-card-text class="d-flex">
														<v-spacer></v-spacer>
														<div v-html="qrCode"></div>
														<v-spacer></v-spacer>
													</v-card-text>
													<v-card-subtitle class="pb-5 text-center"
														>Scan QR code in Authenticator-App
													</v-card-subtitle>
												</div>
												<v-divider
													vertical
													style="border-color: unset !important"
													class="mx-5"
												/>
												<div class="pb-5">
													<v-card-title> Recovery Codes</v-card-title>
													<div v-for="(code, i) in recoveryCodes" :key="i">
														<v-chip variant="flat" class="mt-2"
															>{{ code }}
														</v-chip>
													</div>
												</div>
											</div>
											<v-divider style="border-color: unset !important" />
											<v-card-actions class="d-flex gap-4 justify-start">
												<v-otp-input
													class="mr-4"
													variant="outlined"
													autofocus
													length="6"
													:error="wrong2FACode"
													v-model="totpCode"
												/>
												<v-btn
													color="secondary"
													variant="flat"
													@click="confirm2FA"
												>
													Submit
												</v-btn>
											</v-card-actions>
											<template v-if="wrong2FACode">
												<v-alert
													type="error"
													variant="tonal"
													density="default"
													class="mb-4 mx-4"
												>
													The code you entered is incorrect. please try again.
												</v-alert>
											</template>
										</v-card>
									</v-col>
									<v-col v-else>
										<show-password-confirmation
											v-if="showSetPasswordConfirmation"
											:is-visible="showSetPasswordConfirmation"
											@close-dialog="showSetPasswordConfirmation = false"
										/>
										<v-alert
											border="start"
											color="error"
											variant="tonal"
											icon="mdi-cellphone-lock"
											prominent
										>
											<div class="d-flex">
												<div class="pt-2">
													We strongly recommend you activate Two Factor
													Authentication for logging in.
												</div>
												<v-spacer></v-spacer>
												<v-btn color="error" @click="enable2FA"
													>Enable 2FA Now
												</v-btn>
											</div>
										</v-alert>
									</v-col>
								</v-row>
								<v-row v-else>
									<v-col cols="12" v-if="user.has2FA">
										User has 2FA enabled
									</v-col>
									<v-col cols="12" v-else>
										2FA is not (yet) enabled by user
									</v-col>
								</v-row>
							</v-expansion-panel-text>
						</v-expansion-panel>
						<v-expansion-panel class="mt-5" v-if="!isProfilePage">
							<v-expansion-panel-title>
								<span class="text-h6">{{
									$t("users.overview.sections.roles")
								}}</span>
							</v-expansion-panel-title>
							<v-expansion-panel-text class="pt-3">
								<v-row>
									<v-col cols="12">
										<div v-for="role in availableRoles" :key="role.name_clean">
											<div class="d-flex align-sm-center align-start">
												<v-avatar
													size="48"
													class="mr-2"
													rounded="md"
													color="lightprimary"
												>
													<i
														:class="`fa-duotone fa-people-group fa-lg text-primary`"
													></i>
												</v-avatar>
												<div class="pl-4">
													<h6 class="text-h6 mb-1 mt-1">
														{{ role.name }}
													</h6>
													<h5 class="text-subtitle-1 text-medium-emphasis">
														{{ role.description }}
													</h5>
												</div>
												<div class="ml-auto mr-1 pa-sm-1 pa-6 pl-7">
													<v-checkbox
														v-model="user.roles"
														color="primary"
														:value="role"
														hide-details
														inset
													/>
												</div>
											</div>
										</div>
									</v-col>
								</v-row>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>
				</v-col>
			</v-row>

			<div class="text-end mt-4">
				<v-btn
					color="primary"
					:disabled="isSaving || isFormValid === false"
					@click="updateUser"
					>{{ $t("users.detail.actionSave") }}
				</v-btn>
			</div>
		</v-form>
	</module-loading>
</template>

<style lang="scss">
.v-expansion-panel-title__overlay {
	background: rgba(var(--v-theme-primary)) !important;
}
</style>
